import { Link } from 'react-router-dom'

export function PrivacyPage() {
  return (
    <div className="container mx-auto px-4 py-20 max-w-4xl">
      <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
      <h2 className="text-2xl text-muted-foreground mb-8">プライバシーポリシー</h2>
      <div className="prose prose-gray max-w-none">
        <p className="text-muted-foreground mb-6">Last updated / 最終更新日: 2025-12-25</p>

        {/* Language Priority */}
        <section className="mb-8 p-4 bg-muted/30 rounded-lg">
          <h2 className="text-xl font-semibold mb-3">Language Priority / 言語の優先</h2>
          <div className="space-y-3">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">English:</span>{' '}
              In case of any discrepancy between the Japanese and English versions of this Privacy Policy,
              the Japanese version shall prevail.
            </p>
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">日本語:</span>{' '}
              本プライバシーポリシーの日本語版と英語版に相違がある場合、日本語版が優先します。
            </p>
          </div>
        </section>

        {/* 1. Information We Collect */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">1. Information We Collect / 収集する情報</h2>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">English:</span>{' '}
                We collect information you provide directly to us when you:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Create an account (email address, name via Google OAuth)</li>
                <li>Use our services (templates, designs, usage data)</li>
                <li>Subscribe to a paid plan (payment information handled by Stripe)</li>
                <li>Contact us for support</li>
              </ul>
            </div>
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                以下の場合に、お客様から直接提供される情報を収集します：
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>アカウントの作成時（Google OAuthを通じたメールアドレス、氏名）</li>
                <li>サービスの利用時（テンプレート、デザイン、利用データ）</li>
                <li>有料プランへのご登録時（支払い情報はStripeが処理）</li>
                <li>サポートへのお問い合わせ時</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 2. How We Use Your Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">2. How We Use Your Information / 情報の利用方法</h2>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">English:</span>{' '}
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Provide, maintain, and improve our services</li>
                <li>Process your transactions and manage your subscription</li>
                <li>Send you technical notices and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Analyze usage patterns to improve user experience</li>
              </ul>
            </div>
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                収集した情報は以下の目的で使用します：
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>サービスの提供、維持、改善</li>
                <li>取引の処理およびサブスクリプションの管理</li>
                <li>技術的な通知およびサポートメッセージの送信</li>
                <li>コメントやご質問への回答</li>
                <li>ユーザー体験向上のための利用パターンの分析</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 3. Service Providers */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">3. Service Providers (Sub-processors) / サービスプロバイダー（委託先）</h2>
          <div className="space-y-4">
            <div>
              <p className="text-muted-foreground mb-3">
                <span className="font-medium text-foreground">English:</span>{' '}
                We share your information with the following third-party service providers who process data on our behalf:
              </p>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full text-sm text-muted-foreground">
                  <thead>
                    <tr className="border-b border-muted">
                      <th className="text-left py-2 pr-4">Provider</th>
                      <th className="text-left py-2 pr-4">Purpose</th>
                      <th className="text-left py-2 pr-4">Data Processed</th>
                      <th className="text-left py-2">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Google (OAuth)</td>
                      <td className="py-2 pr-4">Authentication</td>
                      <td className="py-2 pr-4">Email, name</td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Google (Gemini AI)</td>
                      <td className="py-2 pr-4">AI content generation</td>
                      <td className="py-2 pr-4">Prompts and template content</td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Stripe</td>
                      <td className="py-2 pr-4">Payment processing</td>
                      <td className="py-2 pr-4">Payment info, billing</td>
                      <td className="py-2">USA</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Supabase</td>
                      <td className="py-2 pr-4">Database hosting</td>
                      <td className="py-2 pr-4">Account data, templates</td>
                      <td className="py-2">Japan (Tokyo)</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Cloudflare</td>
                      <td className="py-2 pr-4">CDN, security</td>
                      <td className="py-2 pr-4">IP address, access logs</td>
                      <td className="py-2">Global</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Amplitude</td>
                      <td className="py-2 pr-4">Analytics</td>
                      <td className="py-2 pr-4">Usage events (anonymized)</td>
                      <td className="py-2">USA</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <p className="text-muted-foreground mb-3">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                以下のサードパーティのサービスプロバイダーと、当社に代わってデータを処理するために情報を共有しています：
              </p>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full text-sm text-muted-foreground">
                  <thead>
                    <tr className="border-b border-muted">
                      <th className="text-left py-2 pr-4">プロバイダー</th>
                      <th className="text-left py-2 pr-4">目的</th>
                      <th className="text-left py-2 pr-4">処理データ</th>
                      <th className="text-left py-2">所在地</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Google (OAuth)</td>
                      <td className="py-2 pr-4">認証</td>
                      <td className="py-2 pr-4">メール、氏名</td>
                      <td className="py-2">米国</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Google (Gemini AI)</td>
                      <td className="py-2 pr-4">AIコンテンツ生成</td>
                      <td className="py-2 pr-4">プロンプトとテンプレート内容</td>
                      <td className="py-2">米国</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Stripe</td>
                      <td className="py-2 pr-4">決済処理</td>
                      <td className="py-2 pr-4">支払い情報、請求</td>
                      <td className="py-2">米国</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Supabase</td>
                      <td className="py-2 pr-4">データベースホスティング</td>
                      <td className="py-2 pr-4">アカウントデータ、テンプレート</td>
                      <td className="py-2">日本（東京）</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Cloudflare</td>
                      <td className="py-2 pr-4">CDN、セキュリティ</td>
                      <td className="py-2 pr-4">IPアドレス、アクセスログ</td>
                      <td className="py-2">グローバル</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Amplitude</td>
                      <td className="py-2 pr-4">分析</td>
                      <td className="py-2 pr-4">使用イベント（匿名化）</td>
                      <td className="py-2">米国</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="p-3 bg-muted/30 rounded">
              <p className="text-muted-foreground mb-2">
                <strong>Note on AI Data Usage:</strong> Data sent to Google Gemini AI is used solely for
                generating content in response to your requests. Under our API agreement with Google,
                your data is not used for training AI models.
              </p>
              <p className="text-muted-foreground">
                <strong>AIデータ利用について:</strong> Google Gemini AIに送信されるデータは、お客様のリクエストに応じたコンテンツ生成のためにのみ使用されます。GoogleとのAPI契約に基づき、お客様のデータはAIモデルのトレーニングには使用されません。
              </p>
            </div>
          </div>
        </section>

        {/* 4. International Data Transfers */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">4. International Data Transfers / 国外へのデータ移転</h2>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">English:</span>{' '}
                Your personal data may be transferred to and processed in countries outside of Japan,
                including the United States. These countries may have data protection laws different from Japan.
                When transferring data internationally, we ensure appropriate safeguards are in place, including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Contractual clauses with service providers</li>
                <li>Use of providers with recognized data protection certifications</li>
                <li>Technical measures to protect data in transit</li>
              </ul>
            </div>
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                お客様の個人データは、米国を含む日本国外の国に移転され、処理される場合があります。これらの国のデータ保護法は日本と異なる場合があります。国際的にデータを移転する際は、以下を含む適切な保護措置を講じています：
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>サービスプロバイダーとの契約条項</li>
                <li>認定されたデータ保護認証を持つプロバイダーの使用</li>
                <li>転送中のデータを保護する技術的措置</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 5. Information Sharing */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">5. Information Sharing / 情報の共有</h2>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">English:</span>{' '}
                We do not sell your personal information. We share your information only in the following cases:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li><strong>Service Providers:</strong> As listed in Section 3 above</li>
                <li><strong>Analytics:</strong> Aggregated, anonymized data for service improvement</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </div>
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                お客様の個人情報を販売することはありません。以下の場合にのみ情報を共有します：
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li><strong>サービスプロバイダー:</strong> 上記セクション3に記載のとおり</li>
                <li><strong>分析:</strong> サービス改善のための集約・匿名化されたデータ</li>
                <li><strong>法的要件:</strong> 法律で要求される場合、または当社の権利を保護するために必要な場合</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 6. Data Security */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">6. Data Security / データセキュリティ</h2>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">English:</span>{' '}
                We implement appropriate technical and organizational measures to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Encryption of data in transit (TLS/HTTPS)</li>
                <li>Encryption of data at rest (provided by our database hosting provider)</li>
                <li>Access controls and authentication</li>
                <li>Use of reputable third-party infrastructure providers with security certifications</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                However, no method of transmission over the Internet is 100% secure,
                and we cannot guarantee absolute security.
              </p>
            </div>
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                お客様の個人情報を保護するため、以下を含む適切な技術的・組織的措置を講じています：
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>転送中のデータの暗号化（TLS/HTTPS）</li>
                <li>保存データの暗号化（データベースホスティングプロバイダーによる提供）</li>
                <li>アクセス制御と認証</li>
                <li>セキュリティ認証を取得した信頼性の高いサードパーティインフラプロバイダーの利用</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                ただし、インターネット上での送信方法は100%安全ではなく、絶対的なセキュリティを保証することはできません。
              </p>
            </div>
          </div>
        </section>

        {/* 7. Data Retention */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">7. Data Retention / データの保持</h2>
          <div className="space-y-4">
            <div>
              <p className="text-muted-foreground mb-3">
                <span className="font-medium text-foreground">English:</span>{' '}
                We retain your personal information based on the following guidelines:
              </p>
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full text-sm text-muted-foreground">
                  <thead>
                    <tr className="border-b border-muted">
                      <th className="text-left py-2 pr-4">Data Type</th>
                      <th className="text-left py-2">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Account data</td>
                      <td className="py-2">While account is active + 30 days after deletion request</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Templates & designs</td>
                      <td className="py-2">While account is active</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Usage logs</td>
                      <td className="py-2">90 days</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Analytics data</td>
                      <td className="py-2">24 months (anonymized)</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Billing records</td>
                      <td className="py-2">7 years (as required by law)</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Support correspondence</td>
                      <td className="py-2">3 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <p className="text-muted-foreground mb-3">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                以下のガイドラインに基づいて個人情報を保持します：
              </p>
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full text-sm text-muted-foreground">
                  <thead>
                    <tr className="border-b border-muted">
                      <th className="text-left py-2 pr-4">データの種類</th>
                      <th className="text-left py-2">保持期間</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">アカウントデータ</td>
                      <td className="py-2">アカウントが有効な間 + 削除リクエスト後30日</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">テンプレート・デザイン</td>
                      <td className="py-2">アカウントが有効な間</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">使用ログ</td>
                      <td className="py-2">90日</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">分析データ</td>
                      <td className="py-2">24ヶ月（匿名化）</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">請求記録</td>
                      <td className="py-2">7年（法令要件）</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">サポート対応履歴</td>
                      <td className="py-2">3年</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-muted-foreground">
              You may request deletion of your account and associated data at any time through
              our <Link to="/contact" className="underline">contact form</Link>.
              <br />
              お問い合わせフォームを通じて、いつでもアカウントおよび関連データの削除をリクエストできます。
            </p>
          </div>
        </section>

        {/* 8. Your Rights */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">8. Your Rights / お客様の権利</h2>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">English:</span>{' '}
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Export your data in a portable format</li>
                <li>Object to or restrict certain processing</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </div>
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                お客様には以下の権利があります：
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>個人情報へのアクセス</li>
                <li>不正確なデータの訂正</li>
                <li>データの削除のリクエスト</li>
                <li>ポータブルな形式でのデータのエクスポート</li>
                <li>特定の処理への異議または制限</li>
                <li>該当する場合の同意の撤回</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 9. Cookies and Tracking Technologies */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">9. Cookies and Tracking Technologies / クッキーおよびトラッキング技術</h2>
          <div className="space-y-4">
            <div>
              <p className="text-muted-foreground mb-3">
                <span className="font-medium text-foreground">English:</span>{' '}
                We use cookies and similar technologies for the following purposes:
              </p>
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full text-sm text-muted-foreground">
                  <thead>
                    <tr className="border-b border-muted">
                      <th className="text-left py-2 pr-4">Cookie Type</th>
                      <th className="text-left py-2 pr-4">Purpose</th>
                      <th className="text-left py-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Essential</td>
                      <td className="py-2 pr-4">Authentication, session management</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">Functional</td>
                      <td className="py-2 pr-4">Language preferences, settings</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">Analytics</td>
                      <td className="py-2 pr-4">Usage analysis (Amplitude)</td>
                      <td className="py-2">24 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <p className="text-muted-foreground mb-3">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                以下の目的でクッキーおよび類似の技術を使用しています：
              </p>
              <div className="overflow-x-auto mb-3">
                <table className="min-w-full text-sm text-muted-foreground">
                  <thead>
                    <tr className="border-b border-muted">
                      <th className="text-left py-2 pr-4">クッキーの種類</th>
                      <th className="text-left py-2 pr-4">目的</th>
                      <th className="text-left py-2">有効期間</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">必須</td>
                      <td className="py-2 pr-4">認証、セッション管理</td>
                      <td className="py-2">セッション</td>
                    </tr>
                    <tr className="border-b border-muted/50">
                      <td className="py-2 pr-4">機能</td>
                      <td className="py-2 pr-4">言語設定、設定</td>
                      <td className="py-2">1年</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4">分析</td>
                      <td className="py-2 pr-4">使用状況分析（Amplitude）</td>
                      <td className="py-2">24ヶ月</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-muted-foreground">
              You can control cookies through your browser settings. Disabling essential cookies may affect Service functionality.
              <br />
              ブラウザの設定からクッキーを制御できます。必須クッキーを無効にすると、サービスの機能に影響する場合があります。
            </p>
          </div>
        </section>

        {/* 10. Sensitive Information */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">10. Sensitive Information / 機密情報</h2>
          <div className="space-y-3">
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">English:</span>{' '}
                Please do not input the following types of information into the Service, particularly when using AI features:
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>Personal identification numbers (e.g., My Number, passport numbers)</li>
                <li>Financial account information (beyond what's needed for payment)</li>
                <li>Medical or health information</li>
                <li>Confidential business information</li>
                <li>Any information you would not want to share publicly</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                We are not responsible for any sensitive information you choose to input into the Service.
              </p>
            </div>
            <div>
              <p className="text-muted-foreground mb-2">
                <span className="font-medium text-foreground">日本語:</span>{' '}
                本サービス、特にAI機能を使用する際には、以下の種類の情報を入力しないでください：
              </p>
              <ul className="list-disc list-inside text-muted-foreground ml-4 space-y-1">
                <li>個人識別番号（マイナンバー、パスポート番号など）</li>
                <li>財務口座情報（支払いに必要な情報を除く）</li>
                <li>医療または健康情報</li>
                <li>機密ビジネス情報</li>
                <li>公開したくない情報</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                お客様が本サービスに入力を選択した機密情報については、当社は責任を負いません。
              </p>
            </div>
          </div>
        </section>

        {/* 11. Children's Privacy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">11. Children's Privacy / 児童のプライバシー</h2>
          <div className="space-y-3">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">English:</span>{' '}
              The Service is not intended for children under 18 years of age. We do not knowingly
              collect personal information from children. If we learn that we have collected personal
              information from a child, we will take steps to delete such information.
            </p>
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">日本語:</span>{' '}
              本サービスは18歳未満の児童を対象としていません。児童から故意に個人情報を収集することはありません。児童から個人情報を収集したことが判明した場合、当該情報を削除する措置を講じます。
            </p>
          </div>
        </section>

        {/* 12. Changes to This Policy */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">12. Changes to This Policy / ポリシーの変更</h2>
          <div className="space-y-3">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">English:</span>{' '}
              We may update this Privacy Policy from time to time. We will notify you of any material
              changes by posting the new policy on this page, updating the "Last updated" date,
              and, where appropriate, sending you an email notification.
            </p>
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">日本語:</span>{' '}
              本プライバシーポリシーは随時更新される場合があります。重要な変更がある場合は、本ページに新しいポリシーを掲載し、「最終更新日」を更新することでお知らせします。また、必要に応じてメールでも通知します。
            </p>
          </div>
        </section>

        {/* 13. Contact Us */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">13. Contact Us / お問い合わせ</h2>
          <div className="space-y-3">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">English:</span>{' '}
              If you have any questions about this Privacy Policy or wish to exercise your data rights,
              please contact us through our{' '}
              <Link to="/contact" className="underline">contact form</Link>.
            </p>
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">日本語:</span>{' '}
              本プライバシーポリシーについてご質問がある場合、またはデータに関する権利を行使したい場合は、
              <Link to="/contact" className="underline">お問い合わせフォーム</Link>よりご連絡ください。
            </p>
          </div>
          <div className="mt-4 text-muted-foreground">
            <p>Email: support@digemhq.com</p>
            <p>Contact Form: <a href="https://digemhq.com/contact" className="underline">https://digemhq.com/contact</a></p>
          </div>
        </section>
      </div>
    </div>
  )
}
